import { useEffect } from 'react';
import {
  getGroupedPickleballMarkerIconHtml,
  getMarkerIconHtml,
  getPlaceMarkerIconHtml,
  getUserLocationMarkerHtml,
} from '../features/naver-discover/markerIcons';

const useNaverDiscoverMapMarkers = ({
  naverMapsStatus,
  mapInstanceRef,
  markerRefs,
  userLocationMarkerRef,
  suppressFitBoundsUntilRef,
  suppressAutoRecenterUntilRef,
  nearbyEvents,
  places,
  placeEventMap,
  userLocation,
  getEventTypeIconLabel,
  eventTypeStyles,
  onUserLocationClick,
  onSelectPlaceGroup,
  onSelectEvent,
}) => {
  useEffect(() => {
    if (naverMapsStatus !== 'ready') return;
    if (!window.naver?.maps) return;
    const map = mapInstanceRef.current;
    if (!map) return;

    if (userLocation) {
      const naver = window.naver;
      const position = new naver.maps.LatLng(userLocation.lat, userLocation.lng);
      if (!userLocationMarkerRef.current) {
        userLocationMarkerRef.current = new naver.maps.Marker({
          position,
          map,
          title: '내 위치',
          icon: {
            content: getUserLocationMarkerHtml(),
            anchor: new naver.maps.Point(19, 48),
          },
        });

        if (typeof onUserLocationClick === 'function') {
          userLocationMarkerRef.current.__clickListener = naver.maps.Event.addListener(
            userLocationMarkerRef.current,
            'click',
            () => {
              onUserLocationClick();
            }
          );
        }
      } else {
        userLocationMarkerRef.current.setPosition(position);
        userLocationMarkerRef.current.setMap(map);
      }
    } else if (userLocationMarkerRef.current) {
      try {
        userLocationMarkerRef.current.setMap(null);
      } catch {
        // ignore
      }
    }
  }, [mapInstanceRef, naverMapsStatus, onUserLocationClick, suppressAutoRecenterUntilRef, userLocation, userLocationMarkerRef]);

  useEffect(() => {
    if (naverMapsStatus !== 'ready') return;
    if (!window.naver?.maps) return;
    const map = mapInstanceRef.current;
    if (!map) return;

    if (Date.now() < (suppressFitBoundsUntilRef.current || 0)) return;

    markerRefs.current.forEach((m) => {
      try {
        m.setMap(null);
      } catch {
        // ignore
      }
    });
    markerRefs.current = [];

    const naver = window.naver;
    const bounds = new naver.maps.LatLngBounds();

    const toKey = (event) => {
      const lat = Number(event?.latitude);
      const lng = Number(event?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return `${lat.toFixed(6)}|${lng.toFixed(6)}`;
    };

    const grouped = new Map();
    (nearbyEvents || []).forEach((event) => {
      const key = toKey(event);
      if (!key) return;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(event);
    });

    grouped.forEach((groupEvents) => {
      const first = groupEvents[0];
      const position = new naver.maps.LatLng(first.latitude, first.longitude);
      bounds.extend(position);

      const isGroup = groupEvents.length > 1;
      const matchPlaceForSingle = !isGroup
        ? (places || []).find((place) => {
            const latDiff = Math.abs(place.latitude - first.latitude);
            const lngDiff = Math.abs(place.longitude - first.longitude);
            return latDiff < 0.00015 && lngDiff < 0.00015;
          })
        : null;

      const marker = new naver.maps.Marker({
        position,
        map,
        title: String(first.venueName || first.location || first.title || ''),
        icon: {
          content: isGroup
            ? getGroupedPickleballMarkerIconHtml(groupEvents.length)
            : getMarkerIconHtml({
                eventType: first.eventType || 'open_game',
                badgeText: matchPlaceForSingle ? String(groupEvents.length) : '',
                showBadge: Boolean(matchPlaceForSingle),
                getEventTypeIconLabel,
                eventTypeStyles,
              }),
          anchor: new naver.maps.Point(19, 48),
        },
      });

      naver.maps.Event.addListener(marker, 'click', () => {
        suppressFitBoundsUntilRef.current = Date.now() + 15000;
        suppressAutoRecenterUntilRef.current = Date.now() + 15000;

        if (groupEvents.length > 1) {
          const matchPlace = (places || []).find((place) => {
            const latDiff = Math.abs(place.latitude - first.latitude);
            const lngDiff = Math.abs(place.longitude - first.longitude);
            return latDiff < 0.00015 && lngDiff < 0.00015;
          });
          onSelectPlaceGroup?.(matchPlace || null, groupEvents);
          return;
        }

        if (matchPlaceForSingle) {
          onSelectPlaceGroup?.(matchPlaceForSingle, groupEvents);
          return;
        }

        onSelectEvent?.(first.id);
      });

      markerRefs.current.push(marker);
    });

    (places || []).forEach((place) => {
      const position = new naver.maps.LatLng(place.latitude, place.longitude);
      bounds.extend(position);
      const eventsAtPlace = placeEventMap?.get(place.id) || [];
      if (eventsAtPlace.length > 0) return;

      const marker = new naver.maps.Marker({
        position,
        map,
        title: String(place?.name || '장소'),
        icon: {
          content: getPlaceMarkerIconHtml(eventsAtPlace.length ? String(eventsAtPlace.length) : ''),
          anchor: new naver.maps.Point(19, 48),
        },
      });

      naver.maps.Event.addListener(marker, 'click', () => {
        suppressFitBoundsUntilRef.current = Date.now() + 15000;
        suppressAutoRecenterUntilRef.current = Date.now() + 15000;
        onSelectPlaceGroup?.(place, eventsAtPlace);
      });

      markerRefs.current.push(marker);
    });

    if ((nearbyEvents || []).length > 0) {
      map.fitBounds(bounds);
    }
  }, [
    eventTypeStyles,
    getEventTypeIconLabel,
    mapInstanceRef,
    markerRefs,
    naverMapsStatus,
    nearbyEvents,
    onSelectEvent,
    onSelectPlaceGroup,
    placeEventMap,
    places,
    suppressAutoRecenterUntilRef,
    suppressFitBoundsUntilRef,
  ]);
};

export default useNaverDiscoverMapMarkers;
